/**
 * Bestellingen Historie - Overzicht van verstuurde bestellingen
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bestellingenService, BestellingListItem, BestellingStatus } from '../../services/bestellingen.service';

const BestellingenHistorie = () => {
  const navigate = useNavigate();
  const [bestellingen, setBestellingen] = useState<BestellingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BestellingStatus | ''>('');
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  const loadBestellingen = useCallback(async () => {
    try {
      setLoading(true);
      // Haal alle niet-concept bestellingen op
      const data = await bestellingenService.getAll(statusFilter || undefined);
      // Filter alleen besteld, ontvangen, geannuleerd (geen concept)
      const filtered = data.filter(b =>
        statusFilter ? b.status === statusFilter : ['besteld', 'ontvangen', 'geannuleerd'].includes(b.status)
      );
      setBestellingen(filtered);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de bestellingen');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadBestellingen();
  }, [loadBestellingen]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!bestellingen.length) return;

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185;
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll();
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [bestellingen]);

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-NL');
  };

  const handleMarkeerOntvangen = async (id: number) => {
    try {
      await bestellingenService.markeerAlsOntvangen(id);
      await loadBestellingen();
    } catch (err: any) {
      alert(err.message || 'Fout bij het markeren als ontvangen');
    }
  };

  const getStatusBadge = (status: BestellingStatus) => {
    const styles: Record<BestellingStatus, { bg: string; color: string }> = {
      concept: { bg: '#fef3c7', color: '#92400e' },
      besteld: { bg: '#dbeafe', color: '#1e40af' },
      onderweg: { bg: '#e0e7ff', color: '#3730a3' },
      ontvangen: { bg: '#d1fae5', color: '#065f46' },
      geannuleerd: { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = styles[status] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Bestellingen laden...</div>
      </div>
    );
  }

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#495057'
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Bestellingen</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/bestellingen/new')}
        >
          + Nieuwe Bestelling
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters-bar" style={{ marginBottom: '1rem' }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as BestellingStatus | '')}
          className="filter-select"
        >
          <option value="">Alle statussen</option>
          <option value="besteld">Besteld</option>
          <option value="ontvangen">Ontvangen</option>
          <option value="geannuleerd">Geannuleerd</option>
        </select>
      </div>

      {/* Header */}
      {bestellingen.length > 0 && (
        <div ref={tableHeaderRef} style={{
          ...headerStyle,
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Bestelnr.</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Referentie</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Leverdatum</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
          <div style={{ flex: '1', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header */}
      {isHeaderSticky && (
        <div style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          ...headerStyle,
          borderRadius: '8px 8px 0 0'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Bestelnr.</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Referentie</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Leverdatum</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
          <div style={{ flex: '1', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* List */}
      {bestellingen.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {bestellingen.map((bestelling) => (
            <div key={bestelling.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6'
            }}>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.bestelnummer || '-'}
              </div>
              <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.leverancier_naam || '-'}
              </div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.referentie || '-'}
              </div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>
                {formatDate(bestelling.verwachte_leverdatum)}
              </div>
              <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>
                {getStatusBadge(bestelling.status)}
              </div>
              <div style={{ flex: '0.6', minWidth: 0, fontWeight: 600, paddingRight: '1rem' }}>
                {formatCurrency(bestelling.totaal_incl_btw)}
              </div>
              <div style={{ flex: '1', minWidth: 0, display: 'flex', gap: '0.25rem', flexWrap: 'nowrap', alignItems: 'center' }}>
                {bestelling.status === 'besteld' && (
                  <button
                    className="btn btn-sm"
                    onClick={() => handleMarkeerOntvangen(bestelling.id)}
                    style={{
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem'
                    }}
                  >
                    Ontvangen
                  </button>
                )}
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => navigate(`/bestelpool/${bestelling.id}`)}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Bekijken
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {bestellingen.length === 0 && !loading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen bestellingen gevonden
        </div>
      )}

      {bestellingen.length > 0 && (
        <div className="table-footer" style={{ marginTop: '1rem' }}>
          <p style={{ margin: 0 }}>{bestellingen.length} bestelling(en) gevonden</p>
        </div>
      )}
    </div>
  );
};

export default BestellingenHistorie;
